import React, { useEffect, useMemo, useState } from "react";
import { getAttendance, getFees, getSalaries, getStudent, getTeacher, updateStudent, updateTeacher } from "../services/api";
import { showAppToast } from "../utils/feedback";
import { getFeeCoverageForMonth } from "../utils/feeCoverage";
import { formatDate } from "../utils/helpers";
import "./GlobalProfileModal.css";

const getTodayDate = () => new Date().toISOString().split("T")[0];
const getCurrentMonth = () => getTodayDate().slice(0, 7);
const readJpegFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg"];
    const lowerName = file.name.toLowerCase();
    const hasValidExtension = lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg");

    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      reject(new Error("Only JPG and JPEG images are allowed"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });

function GlobalProfileModal({ item, onClose, onEdit, onDelete }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [feeRecords, setFeeRecords] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);

  useEffect(() => {
    if (!item) {
      setProfile(null);
      setTodayAttendance([]);
      setFeeRecords([]);
      setSalaryRecords([]);
      return;
    }

    const loadProfile = async () => {
      try {
        setLoading(true);
        const today = getTodayDate();

        if (item.type === "student") {
          const [studentRes, attendanceRes, feesRes] = await Promise.all([
            getStudent(item.id, { includeImages: true }),
            getAttendance(today, "all"),
            getFees({})
          ]);

          const nextProfile = studentRes.data || null;
          setProfile(nextProfile);
          setEditForm({
            name: nextProfile?.name || "",
            class: nextProfile?.class || "",
            rollNumber: nextProfile?.rollNumber || "",
            parentPhone: nextProfile?.parentPhone || "",
            imageData: nextProfile?.imageData || nextProfile?.imageUrl || ""
          });
          setTodayAttendance(
            attendanceRes.data.filter((record) => String(record.student_id) === item.id)
          );
          setFeeRecords(feesRes.data.filter((record) => String(record.student_id) === item.id));
          setSalaryRecords([]);
          return;
        }

        const [teacherRes, salaryRes] = await Promise.all([getTeacher(item.id, { includeImages: true }), getSalaries({})]);
        const nextProfile = teacherRes.data || null;
        setProfile(nextProfile);
        setEditForm({
          name: nextProfile?.name || "",
          subject: nextProfile?.subject || "",
          phone: nextProfile?.phone || "",
          experience: nextProfile?.experience || "",
          imageData: nextProfile?.imageData || nextProfile?.imageUrl || ""
        });
        setSalaryRecords(salaryRes.data.filter((record) => String(record.teacher_id) === item.id));
        setTodayAttendance([]);
        setFeeRecords([]);
      } catch (err) {
        console.error("Failed to load global profile:", err);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [item]);

  useEffect(() => {
    setIsEditing(false);
    setSaving(false);
  }, [item]);

  const imageSource = profile?.imageData || profile?.imageUrl;
  const todayAttendanceStatus = todayAttendance[0]?.attendance_status || "not marked";
  const currentMonth = getCurrentMonth();
  const currentMonthFeeCoverage = useMemo(
    () => getFeeCoverageForMonth(feeRecords, currentMonth),
    [feeRecords, currentMonth]
  );
  const currentMonthFeeRecord = currentMonthFeeCoverage.currentRecord;
  const currentMonthSalaryRecord = salaryRecords.find((record) => String(record.payPeriod || "").slice(0, 7) === currentMonth);
  const currentMonthFeePending = item?.type === "student" && !currentMonthFeeCoverage.isPaid;
  const currentMonthSalaryPending = item?.type === "teacher" && (!currentMonthSalaryRecord || currentMonthSalaryRecord.status !== "paid");
  const paidFees = feeRecords.filter((record) => record.status === "paid");
  const paidSalary = salaryRecords.filter((record) => record.status === "paid");

  const financeSummary = useMemo(() => {
    if (item?.type === "student") {
      return [
        {
          label: "Current Month Fee",
          value: currentMonthFeePending ? "Pending" : "Paid",
          helper: currentMonthFeeCoverage.helper
        },
        {
          label: "Paid Fees",
          value: paidFees.length,
          helper: paidFees.length ? `Latest paid ${formatDate(paidFees[0].paidDate)}` : "No paid fee"
        }
      ];
    }

    return [
      {
        label: "Current Month Salary",
        value: currentMonthSalaryPending ? "Not Paid" : "Paid",
        helper: currentMonthSalaryRecord
          ? `Status ${currentMonthSalaryRecord.status} for ${currentMonth}`
          : `No salary record for ${currentMonth}`
      },
      {
        label: "Paid Salary",
        value: paidSalary.length,
        helper: paidSalary.length ? `Latest paid ${formatDate(paidSalary[0].paidDate)}` : "No paid salary"
      }
    ];
  }, [
    currentMonth,
    currentMonthFeeCoverage,
    currentMonthFeePending,
    currentMonthSalaryPending,
    currentMonthSalaryRecord,
    item,
    paidFees,
    paidSalary
  ]);

  if (!item) {
    return null;
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];

    try {
      const imageData = await readJpegFile(file);
      setEditForm((current) => ({ ...current, imageData }));
    } catch (err) {
      showAppToast({ type: "error", title: "Image upload failed", message: err.message });
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);

      if (item.type === "student") {
        await updateStudent(item.id, {
          name: editForm.name,
          class: editForm.class,
          rollNumber: editForm.rollNumber,
          parentPhone: editForm.parentPhone,
          imageData: editForm.imageData
        });

        setProfile((current) => ({
          ...current,
          name: editForm.name,
          class: editForm.class,
          rollNumber: editForm.rollNumber,
          parentPhone: editForm.parentPhone,
          imageData: editForm.imageData
        }));
      } else {
        await updateTeacher(item.id, {
          name: editForm.name,
          subject: editForm.subject,
          phone: editForm.phone,
          experience: editForm.experience,
          imageData: editForm.imageData
        });

        setProfile((current) => ({
          ...current,
          name: editForm.name,
          subject: editForm.subject,
          phone: editForm.phone,
          experience: editForm.experience,
          imageData: editForm.imageData
        }));
      }

      setIsEditing(false);
      showAppToast({
        type: "success",
        title: "Profile updated",
        message: "The profile details were saved successfully."
      });
    } catch (err) {
      console.error("Failed to save profile:", err);
      showAppToast({
        type: "error",
        title: "Failed to save profile",
        message: err.response?.data?.error || err.message || "The profile could not be updated."
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="global-profile-modal">
      <div className="global-profile-modal__backdrop" onClick={onClose} />
      <div className="global-profile-modal__panel">
        <div className="global-profile-modal__header">
          <div className="global-profile-modal__identity">
            {imageSource ? (
              <img src={imageSource} alt={profile?.name || item.title} className="global-profile-modal__avatar" />
            ) : (
              <div className="global-profile-modal__avatar global-profile-modal__avatar--placeholder">
                {(profile?.name || item.title)?.slice(0, 1)?.toUpperCase()}
              </div>
            )}
            <div>
              <span className={`global-profile-modal__tag global-profile-modal__tag--${item.type}`}>
                {item.type === "student" ? "Student Profile" : "Teacher Profile"}
              </span>
              <h2>{profile?.name || item.title}</h2>
              <p>
                {item.type === "student"
                  ? `Class ${profile?.class || item.filterValue} | Roll ${profile?.rollNumber || "-"}`
                  : `${profile?.subject || item.filterValue} | ${profile?.experience || "-"}`
                }
              </p>
            </div>
          </div>
          <button className="global-profile-modal__close" onClick={onClose}>
            X
          </button>
        </div>

        {loading && <p className="global-profile-modal__empty">Loading profile...</p>}

        {!loading && profile && (
          <>
            {(currentMonthFeePending || currentMonthSalaryPending) && (
              <div className="global-profile-modal__alert">
                <strong>{item.type === "student" ? "Fees Pending" : "Salary Not Paid"}</strong>
                <span>
                  {item.type === "student"
                    ? currentMonthFeeRecord
                      ? `Current month fee is covered by ${currentMonthFeeRecord.billingCycle || "monthly"} record from ${formatDate(currentMonthFeeRecord.dueDate)} and is marked ${currentMonthFeeRecord.status}.`
                      : `No fee record found for ${currentMonth}.`
                    : currentMonthSalaryRecord
                      ? `Current month salary is marked ${currentMonthSalaryRecord.status} for ${currentMonth}.`
                      : `No salary record found for ${currentMonth}.`}
                </span>
              </div>
            )}

            <div className="global-profile-modal__actions">
              {!isEditing ? (
                <button className="global-profile-modal__btn global-profile-modal__btn--primary" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
              ) : (
                <>
                  <button className="global-profile-modal__btn global-profile-modal__btn--primary" onClick={handleSaveEdit} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                  <button className="global-profile-modal__btn global-profile-modal__btn--secondary" onClick={() => setIsEditing(false)} disabled={saving}>
                    Cancel
                  </button>
                </>
              )}
              <button className="global-profile-modal__btn global-profile-modal__btn--danger" onClick={() => onDelete(item)}>
                Delete
              </button>
            </div>

            <div className="global-profile-modal__grid">
              <section className="global-profile-modal__card">
                <span className="global-profile-modal__label">Basic Details</span>
                {!isEditing && item.type === "student" ? (
                  <>
                    <p><strong>Name:</strong> {profile.name}</p>
                    <p><strong>Class:</strong> {profile.class}</p>
                    <p><strong>Roll Number:</strong> {profile.rollNumber}</p>
                    <p><strong>Parent Phone:</strong> {profile.parentPhone}</p>
                  </>
                ) : !isEditing ? (
                  <>
                    <p><strong>Name:</strong> {profile.name}</p>
                    <p><strong>Subject:</strong> {profile.subject}</p>
                    <p><strong>Phone:</strong> {profile.phone}</p>
                    <p><strong>Experience:</strong> {profile.experience}</p>
                  </>
                ) : item.type === "student" ? (
                  <div className="global-profile-modal__form">
                    <label>
                      Name
                      <input name="name" value={editForm.name || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Class
                      <input name="class" value={editForm.class || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Roll Number
                      <input name="rollNumber" value={editForm.rollNumber || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Parent Phone
                      <input name="parentPhone" value={editForm.parentPhone || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Profile Image
                      <input type="file" accept=".jpg,.jpeg,image/jpeg" onChange={handleImageChange} />
                    </label>
                    {editForm.imageData && (
                      <img src={editForm.imageData} alt="Profile preview" className="global-profile-modal__preview" />
                    )}
                  </div>
                ) : (
                  <div className="global-profile-modal__form">
                    <label>
                      Name
                      <input name="name" value={editForm.name || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Subject
                      <input name="subject" value={editForm.subject || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Phone
                      <input name="phone" value={editForm.phone || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Experience
                      <input name="experience" value={editForm.experience || ""} onChange={handleEditChange} />
                    </label>
                    <label>
                      Profile Image
                      <input type="file" accept=".jpg,.jpeg,image/jpeg" onChange={handleImageChange} />
                    </label>
                    {editForm.imageData && (
                      <img src={editForm.imageData} alt="Profile preview" className="global-profile-modal__preview" />
                    )}
                  </div>
                )}
              </section>

              <section className="global-profile-modal__card">
                <span className="global-profile-modal__label">Current Date Status</span>
                {item.type === "student" ? (
                  <>
                    <div className={`global-profile-modal__status global-profile-modal__status--${todayAttendanceStatus.replace(" ", "-")}`}>
                      {todayAttendanceStatus}
                    </div>
                    <p>Attendance date: {formatDate(getTodayDate())}</p>
                    <p>
                      {todayAttendance.length > 0
                        ? `Student is marked ${todayAttendanceStatus} for today.`
                        : "Attendance not marked for today."}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="global-profile-modal__status global-profile-modal__status--neutral">active</div>
                    <p>Date: {formatDate(getTodayDate())}</p>
                    <p>Teacher attendance is not tracked in the current module.</p>
                  </>
                )}
              </section>

              {financeSummary.map((summary) => (
                <section className="global-profile-modal__card" key={summary.label}>
                  <span className="global-profile-modal__label">{summary.label}</span>
                  <strong className="global-profile-modal__metric">{summary.value}</strong>
                  <p>{summary.helper}</p>
                </section>
              ))}
            </div>

            {item.type === "student" && (
              <section className="global-profile-modal__section">
                <div className="global-profile-modal__section-head">
                  <h3>Fee Details</h3>
                  <span>{feeRecords.length} records</span>
                </div>
                {feeRecords.length === 0 ? (
                  <p className="global-profile-modal__empty">No fee records found.</p>
                ) : (
                  <div className="global-profile-modal__table-wrap">
                    <table className="global-profile-modal__table">
                      <thead>
                        <tr>
                          <th>Cycle</th>
                          <th>Amount</th>
                          <th>Due Date</th>
                          <th>Status</th>
                          <th>Paid Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeRecords.map((record) => (
                          <tr key={record._id}>
                            <td>{record.billingCycle}</td>
                            <td>{record.amount}</td>
                            <td>{formatDate(record.dueDate)}</td>
                            <td>{record.status}</td>
                            <td>{formatDate(record.paidDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {item.type === "teacher" && (
              <section className="global-profile-modal__section">
                <div className="global-profile-modal__section-head">
                  <h3>Salary Details</h3>
                  <span>{salaryRecords.length} records</span>
                </div>
                {salaryRecords.length === 0 ? (
                  <p className="global-profile-modal__empty">No salary records found.</p>
                ) : (
                  <div className="global-profile-modal__table-wrap">
                    <table className="global-profile-modal__table">
                      <thead>
                        <tr>
                          <th>Pay Period</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Paid Date</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salaryRecords.map((record) => (
                          <tr key={record._id}>
                            <td>{record.payPeriod}</td>
                            <td>{record.amount}</td>
                            <td>{record.status}</td>
                            <td>{formatDate(record.paidDate)}</td>
                            <td>{record.notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {!loading && !profile && <p className="global-profile-modal__empty">Profile details not found.</p>}
      </div>
    </div>
  );
}

export default GlobalProfileModal;
